/**
 * The brief: the decision, the contract, the behaviour and the plan, each
 * rendered with its state read from GitHub (contract.md §6). Approval one is the
 * owner's merge of the brief's pull requests, which each part links.
 *
 * The OpenAPI and AsyncAPI viewers are CHG-9: until then a contract's documents
 * show their operations and channels, with a link to the file.
 */
import { useEffect, useState, type ComponentType, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ClipboardList, ExternalLink, FileText, ScrollText, TestTubes } from 'lucide-react';
import { MarkdownRenderer } from '@/components/docs/MarkdownRenderer';
import { PrChip, StateBadge, TagChip } from '@/components/changes/changeBadges';
import { Block, CODE, Rule, StageSection, TABLE, TD, TH, TableScroll } from '@/components/changes/StageSection';
import '@/styles/docs-shared.css';
import { decisionReaderPath } from '@/lib/designDecisions';
import {
  APPROVALS,
  PART_LABEL,
  PART_WHAT,
  SIZE_RULE,
  countsLine,
  decisionSummary,
  fileUrl,
  pullRequestsOf,
  shapeState,
  withoutTitle,
  type Change,
  type ChangeState,
  type FeatureFile,
  type PartKey,
  type PullRequest,
  type TrackedFile,
} from '@/lib/changes/model';
import { docsApi } from '@/services/docsApi';
import { useDocText } from './useDocText';

const PART_ICON: Record<PartKey, ComponentType<{ className?: string }>> = {
  decision: ScrollText,
  contract: FileText,
  behaviour: TestTubes,
  plan: ClipboardList,
};

/** A file of the repository, rendered as the reader renders it, its own title
 *  left to the part that holds it. */
function Rendered({ text }: { text: string }) {
  return (
    <div className="docs-page mt-3 max-w-none text-sm">
      {/* The reader's gutter for its heading anchors is no use inside a card. */}
      <article className="!ps-0">
        <MarkdownRenderer content={withoutTitle(text)} />
      </article>
    </div>
  );
}

function Files({ change, files }: { change: Change; files: TrackedFile[] }) {
  if (files.length === 0) return null;
  return (
    <ul className="mt-2 space-y-0.5">
      {files.map((file) => (
        <li key={file.path} className="flex flex-wrap items-baseline gap-x-2 text-xs text-slate-600 dark:text-slate-400">
          <code className={`${CODE} break-all`}>{file.path}</code>
          <a href={fileUrl(change, file)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-violet-700 hover:underline dark:text-violet-300">
            the file <ExternalLink className="h-3 w-3" />
          </a>
        </li>
      ))}
    </ul>
  );
}

function PartCard({ part, state, prs, files, change, children }: {
  part: PartKey;
  state: ChangeState;
  prs: PullRequest[];
  files: TrackedFile[];
  change: Change;
  children: ReactNode;
}) {
  const Icon = PART_ICON[part];
  return (
    <article data-part={part} className="rounded-xl border border-slate-200 p-4 dark:border-slate-800">
      <header className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <Icon className="h-4 w-4 text-violet-600 dark:text-violet-300" />
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{PART_LABEL[part]}</h3>
        <StateBadge state={state} />
        <span className="ml-auto flex flex-wrap items-center gap-x-3 gap-y-1">
          {prs.map((pr) => <PrChip key={`${pr.repo}#${pr.number}`} pr={pr} />)}
        </span>
      </header>
      <p className="mt-1.5 text-xs leading-snug text-slate-500 dark:text-slate-400">{PART_WHAT[part]}</p>
      <Files change={change} files={files} />
      <div className="mt-3">{children}</div>
    </article>
  );
}

function DecisionPart({ change }: { change: Change }) {
  const { search } = useLocation();
  const decision = change.decision!;
  const text = useDocText(decision.docId);
  const summary = text ? decisionSummary(text) : '';
  const [reader, setReader] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    if (!decision.docId) return;
    docsApi.getDoc(decision.docId).then(
      (doc) => { if (live) setReader(decisionReaderPath({ repoFullName: doc.repo_full_name, path: doc.path })); },
      () => undefined,  // the title stays text: the record itself is on the page
    );
    return () => { live = false; };
  }, [decision.docId]);

  return (
    <PartCard part="decision" state={change.states.decision ?? 'not yet'} prs={decision.pullRequest ? [decision.pullRequest] : []} files={[change.proposal]} change={change}>
      <div className="rounded-lg bg-slate-50 p-3 dark:bg-slate-950/40">
        <div className="mb-2 flex flex-wrap items-center gap-2 text-sm">
          <code className={CODE}>{decision.adr}</code>
          {reader ? (
            <Link data-decision-title to={{ pathname: reader, search }} className="font-medium text-slate-800 hover:underline dark:text-slate-200">{decision.title}</Link>
          ) : (
            <span className="font-medium text-slate-800 dark:text-slate-200">{decision.title || 'No connected repository holds this record.'}</span>
          )}
          {decision.status && (
            <span data-decision-status className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
              {decision.status}
            </span>
          )}
        </div>
        {summary && <Rendered text={summary} />}
      </div>
    </PartCard>
  );
}

function ContractPart({ change }: { change: Change }) {
  const { files, operations, channels } = change.contract;
  const contractMd = files.find((file) => file.path.endsWith('/contract.md'));
  const text = useDocText(contractMd?.docId);
  return (
    <PartCard part="contract" state={change.states.contract} prs={pullRequestsOf(files)} files={files} change={change}>
      {operations.length > 0 && (
        <TableScroll><table className={TABLE} data-contract="operations">
          <thead>
            <tr>
              <th className={TH}>Operation</th>
              <th className={TH}>What</th>
            </tr>
          </thead>
          <tbody>
            {operations.map((op) => (
              <tr key={`${op.method} ${op.path}`}>
                <td className={`${TD} whitespace-nowrap`}><code className={CODE}>{op.method} {op.path}</code></td>
                <td className={TD}>{op.summary}</td>
              </tr>
            ))}
          </tbody>
        </table></TableScroll>
      )}
      {channels.length > 0 && (
        <TableScroll className="mt-3"><table className={TABLE} data-contract="channels">
          <thead>
            <tr>
              <th className={TH}>Channel</th>
              <th className={TH}>Address</th>
              <th className={TH}>Messages</th>
            </tr>
          </thead>
          <tbody>
            {channels.map((channel) => (
              <tr key={channel.name}>
                <td className={TD}><code className={CODE}>{channel.name}</code></td>
                <td className={TD}>{channel.address}</td>
                <td className={TD}>{channel.messages.join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table></TableScroll>
      )}
      {files.length === 0 && <p className="text-sm text-slate-500 dark:text-slate-400">Not yet: the change holds no contract file.</p>}
      {text && <Rendered text={text} />}
    </PartCard>
  );
}

function ScenarioTable({ file }: { file: FeatureFile }) {
  return (
    <div className="mt-3" data-feature-file={file.path}>
      <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{file.title || file.path}</p>
      <p className="mb-2 flex flex-wrap gap-x-3 text-xs text-slate-500 dark:text-slate-400">
        <span data-scenario-counts>{countsLine(file.counts) || 'No scenario'}</span>
        <span data-runner>{file.runner ? `Runs under ${file.runner}` : 'Names no runner'}</span>
      </p>
      <TableScroll><table className={`${TABLE} min-w-[32rem]`} data-scenarios>
        <thead>
          <tr>
            <th className={TH}>Id</th>
            <th className={TH}>Scenario</th>
            <th className={TH}>State</th>
          </tr>
        </thead>
        <tbody>
          {file.scenarios.map((scenario, i) => (
            <tr key={`${scenario.id}-${i}`} data-scenario={scenario.id}>
              <td className={`${TD} whitespace-nowrap`}>
                {scenario.id ? <code className={CODE}>{scenario.id}</code> : <span className="text-xs text-slate-400">no id</span>}
              </td>
              <td className={TD}>
                {scenario.title}
                <ul className="mt-1 space-y-0.5 font-mono text-[11px] text-slate-500 dark:text-slate-400">
                  {scenario.steps.map((step, j) => <li key={j} data-step>{step}</li>)}
                </ul>
              </td>
              <td className={TD}><TagChip state={scenario.state} /></td>
            </tr>
          ))}
        </tbody>
      </table></TableScroll>
    </div>
  );
}

function BehaviourPart({ change }: { change: Change }) {
  const { files, counts } = change.behaviour;
  return (
    <PartCard part="behaviour" state={change.states.behaviour} prs={pullRequestsOf(files)} files={files} change={change}>
      {files.length === 0 ? (
        <p data-behaviour-hint className="text-sm text-slate-500 dark:text-slate-400">
          No feature file names this change. One comment line naming it, <code className={CODE}># {change.slug}</code>, in a feature file's first ten lines brings the file in.
        </p>
      ) : (
        <>
          <p className="text-xs text-slate-500 dark:text-slate-400" data-all-counts>{countsLine(counts)}</p>
          {files.map((file) => <ScenarioTable key={file.path} file={file} />)}
        </>
      )}
    </PartCard>
  );
}

function PlanPart({ change }: { change: Change }) {
  const { file, draftId, board } = change.plan;
  const text = useDocText(file?.docId);
  return (
    <PartCard part="plan" state={change.states.plan} prs={file ? pullRequestsOf([file]) : []} files={file ? [file] : []} change={change}>
      {board && (
        <p data-board className="mb-2 text-sm text-slate-700 dark:text-slate-300">
          <a href={board.url} target="_blank" rel="noreferrer" className="font-medium text-violet-700 hover:underline dark:text-violet-300">{board.title}</a>
          {': '}{board.items.done} done · {board.items.inProgress} in progress · {board.items.todo} to do
          {board.items.truncated && ' (the first 500 items)'}
        </p>
      )}
      {!file && !draftId && <p className="text-sm text-slate-500 dark:text-slate-400">Not yet: the change holds no plan.md, and no draft is linked to it.</p>}
      {!file && draftId && <p className="text-sm text-slate-500 dark:text-slate-400">A plan draft is linked to this change; plan.md is not written yet.</p>}
      {text && <Rendered text={text} />}
    </PartCard>
  );
}

export function BriefSection({ change, current }: { change: Change; current: boolean }) {
  const prs = [
    ...(change.decision?.pullRequest ? [change.decision.pullRequest] : []),
    ...pullRequestsOf([change.proposal, ...change.contract.files, ...change.behaviour.files, ...(change.plan.file ? [change.plan.file] : [])]),
  ];
  const waiting = prs.filter((pr) => pr.state === 'open');

  return (
    <StageSection stage="shape" title="The brief" state={shapeState(change)} current={current}>
      <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
        <span className="font-medium capitalize text-slate-800 dark:text-slate-200">{change.size}.</span> {SIZE_RULE[change.size]}
      </p>

      <div className="space-y-3">
        {change.decision && <DecisionPart change={change} />}
        <ContractPart change={change} />
        <BehaviourPart change={change} />
        <PlanPart change={change} />
      </div>

      {!change.archived && (
        <Block label={`${APPROVALS.one.label} — ${APPROVALS.one.what}`} className="mt-4 border-violet-200 bg-violet-50/40 dark:border-violet-900/60 dark:bg-violet-950/20">
          <div className="flex flex-wrap items-center gap-2" data-approval="one">
            <StateBadge state={shapeState(change)} />
            <span className="text-sm text-slate-700 dark:text-slate-300">
              {waiting.length > 0 ? 'Merge these pull requests on GitHub to approve:' : APPROVALS.one.how}
            </span>
          </div>
          {waiting.length > 0 && (
            <ul className="mt-2 space-y-1">
              {waiting.map((pr) => <li key={`${pr.repo}#${pr.number}`}><PrChip pr={pr} /></li>)}
            </ul>
          )}
          <Rule>A merge is an approval: nothing on this page approves anything. A comment goes on the pull request, and an edit is a new commit on its branch.</Rule>
        </Block>
      )}
    </StageSection>
  );
}
